import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useAnalyticsContext } from '../../../../context/analytics-context';
import { useInitializationDataContext } from '../../../../context/data-context';
import { FabricTextBox } from '../../../../global-types';
import { useActiveCanvas } from '../../../../hooks';
import {
  getGroupedTextObject,
  CanvasDataTypes,
  convertPointToPixel,
  convertPixelToPoint,
  getFontName,
} from '../../../../utils';
import { Font, FontSelectedOption } from '../font-drawer-types';
import styles from '../font-drawer.module.scss';
import { ALIGNMENT_OPTIONS } from '../utils/constants';
import { findKeyByValue } from '../utils/find-key-by-value';
import { formatFontCollection } from '../utils/format-font-collection';
import { getLabelByFontId } from '../utils/get-label-by-fontid';
import { handleFocus, handleTextTabClick } from '../utils/handle-text-tab-click';
import { Alignment } from './alignment';
import { FontSize } from './font-size';
import { FontStyle } from './font-style';

export const FontTab = (): React.ReactElement => {
  const canvas = useActiveCanvas();
  const textObject: fabric.Textbox = getGroupedTextObject(canvas?.current) as fabric.Textbox;
  const { t } = useTranslation();
  const { updateEditFormats } = useAnalyticsContext();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();

  const [fonts, setFonts] = useState<Font[]>([]);
  const [alignmentIndex, setAlignmentIndex] = useState(findKeyByValue(textObject?.textAlign));

  const originalFontSize = textObject?.fontSize ? convertPixelToPoint(textObject.fontSize) : 30;
  const originalFontFamily = textObject?.fontFamily
    ? getLabelByFontId(textObject?.fontFamily, initializedData?.font_collection.fonts)
    : 'Hey Sunshine';
  const [fontSizeValue, setFontSizeValue] = useState(originalFontSize);
  const [selectedFont, setSelectedFont] = useState(originalFontFamily);
  const activeObject = canvas?.current?.getActiveObject() as FabricTextBox;
  const shouldShowFontResizeControls =
    activeObject.CanEditFontSize ||
    activeObject.data.type === CanvasDataTypes.UserText ||
    activeObject.data.type === CanvasDataTypes.Placeholder;

  const updateCanvasAndFireChange = () => {
    canvas?.current?.renderAll();
    textObject.fire('changed');
  };

  const handleFontStyleChange = (option: FontSelectedOption) => {
    if (textObject) {
      updateEditFormats({ fontName: getFontName(option.id) });
      setSelectedFont(option.label);
      textObject.setOptions({ fontFamily: getFontName(option.id) });
      updateCanvasAndFireChange();
      handleFocus(canvas?.current || null, isMobile, handleTextTabClick);
    }
  };

  const handleFontSizeChange = (action: string) => {
    handleFocus(canvas?.current || null, isMobile, handleTextTabClick);
    if (action === 'increase') {
      setFontSizeValue(fontSizeValue + 1);
    } else {
      setFontSizeValue(fontSizeValue - 1);
    }

    updateEditFormats({ fontSize: fontSizeValue.toString() });
  };

  const handleAlignmentChange = (index: number) => {
    handleFocus(canvas?.current || null, isMobile, handleTextTabClick);
    if (textObject) {
      const textAlignment = (ALIGNMENT_OPTIONS[index.toString()] || 'left').toLowerCase();
      textObject.setOptions({
        textAlign: textAlignment,
      });
      updateEditFormats({ textAlignment: textAlignment });
      updateCanvasAndFireChange();
    }
  };

  useEffect(() => {
    const fontCollection = formatFontCollection(initializedData?.font_collection.fonts);
    if (fontCollection) {
      setFonts(fontCollection);
    }
  }, [initializedData?.font_collection.fonts]);

  useEffect(() => {
    const scaledFontSize = convertPointToPixel(fontSizeValue);
    textObject.setOptions({ fontSize: scaledFontSize });
    updateCanvasAndFireChange();
  }, [fontSizeValue]);

  return (
    <div className={styles['text-tab']} data-testid="font-tab">
      <FontStyle
        addClass={styles['font-style']}
        label={t('fontDrawer.fontStyle')}
        fonts={fonts}
        selectedFont={selectedFont}
        onChange={(option) => handleFontStyleChange(option as FontSelectedOption)}
      />
      <div className={styles['counter-alignment-container']}>
        {shouldShowFontResizeControls && (
          <FontSize
            addClass={styles['font-size']}
            label={t('fontDrawer.fontSize')}
            onClickButton={handleFontSizeChange}
            fontSizeValue={fontSizeValue}
            upper={60}
            lower={5}
            testId="font-size"
          />
        )}
        <Alignment
          label={t('fontDrawer.alignment')}
          addClass={styles.alignment}
          buttonGroupClick={handleAlignmentChange}
          activeIndex={alignmentIndex}
          setActiveIndex={setAlignmentIndex}
        />
      </div>
    </div>
  );
};
